/**
 * Chat widget context for managing global chat widget state
 * Provides expand/collapse functionality accessible throughout the app
 */

import { createContext, useContext, useState, ReactNode } from "react"

interface ChatWidgetContextValue {
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
  toggleExpanded: () => void
}

const ChatWidgetContext = createContext<ChatWidgetContextValue | undefined>(undefined)

interface ChatWidgetProviderProps {
  children: ReactNode
}

/**
 * ChatWidgetProvider component
 * Manages chat widget visibility state across the application
 *
 * @param props - Component props including children
 * @returns Provider wrapper for chat widget context
 */
export function ChatWidgetProvider({ children }: ChatWidgetProviderProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  const value = {
    isExpanded,
    setIsExpanded,
    toggleExpanded,
  }

  return (
    <ChatWidgetContext.Provider value={value}>
      {children}
    </ChatWidgetContext.Provider>
  )
}

/**
 * Hook to access chat widget context
 * Must be used within ChatWidgetProvider
 *
 * @returns Chat widget context value with state and control functions
 */
export function useChatWidget() {
  const context = useContext(ChatWidgetContext)

  if (context === undefined) {
    throw new Error("useChatWidget must be used within a ChatWidgetProvider")
  }

  return context
}
